import { useState, useEffect, useCallback, useRef } from 'react';
import FilterBar from '../../components/FilterBar/FilterBar';
import KPIBox from '../../components/KPIBox/KPIBox';
import AchChart from '../../components/Charts/AchChart';
import SalesTrendChart from '../../components/Charts/SalesTrendChart';
import DataTable from '../../components/Table/DataTable';
import StockistTable from '../../components/StockistTable/StockistTable';
import useFetchData from '../../hooks/useFetchData';
import { useFilters } from '../../context/FilterContext';
import {
  getSalesSummary,
  getMonthPerformance,
  getBDMPerformance,
  getBrandPerformance,
  getHQPerformance,
  getStatePerformance,
  getStockistPerformance,
} from '../../services/api';
import { formatPct, formatNumber } from '../../utils/calculations';
import './Dashboard.css';

// ── Unit formatter ────────────────────────────────────────────────────────────
function formatVal(value, unit) {
  if (value == null) return '0';
  const num = Math.abs(Number(value));
  switch (unit) {
    case 'lakh':  return `₹${(num / 100000).toFixed(2)} L`;
    case 'crore': return `₹${(num / 10000000).toFixed(2)} Cr`;
    default:      return `₹${num.toLocaleString('en-IN')}`;
  }
}

const fmtPct = v => v != null ? `${(+v).toFixed(1)}%` : '0.0%';

// ── Column definitions ────────────────────────────────────────────────────────
const buildMonthlyCols = fmtV => [
  { key: 'month',     label: 'Month',    align: 'left' },
  { key: 'ly_sales',  label: 'LY Sales', align: 'right',  render: fmtV },
  { key: 'target',    label: 'Target',   align: 'right',  render: fmtV },
  { key: 'cy_sales',  label: 'CY Sales', align: 'right',  render: fmtV },
  { key: 'ach_pct',   label: 'Ach %',    align: 'right',  render: fmtPct },
  { key: 'grth_pct',  label: 'Grth %',   align: 'right',  render: fmtPct },
  { key: 'pcpm',      label: 'PCPM',     align: 'right',  render: fmtV },
  { key: 'sop_pct',   label: 'SOP %',    align: 'right',  render: fmtPct },
  { key: 'doi',       label: 'DOI',      align: 'center', render: v => v ?? '0' },
  { key: 'total_cn',  label: 'Total CN', align: 'right',  render: fmtV },
  { key: 'expiry',    label: 'Expiry',   align: 'right',  render: fmtV },
  { key: 'exp_pct',   label: 'Exp %',    align: 'right',  render: fmtPct },
  { key: 'returns',   label: 'Returns',  align: 'right',  render: fmtV },
  { key: 'rate_diff', label: 'Rate Dif', align: 'right',  render: fmtV },
  { key: 'breakage',  label: 'Breakage', align: 'right',  render: fmtV },
];

const buildBdmCols = fmtV => [
  { key: 'bdm',       label: 'BDM',      align: 'left' },
  { key: 'ly_sales',  label: 'LY Sales', align: 'right',  render: fmtV },
  { key: 'target',    label: 'Target',   align: 'right',  render: fmtV },
  { key: 'cy_sales',  label: 'CY Sales', align: 'right',  render: fmtV },
  { key: 'ach_pct',   label: 'Ach %',    align: 'right',  render: fmtPct },
  { key: 'grth_pct',  label: 'Grth %',   align: 'right',  render: fmtPct },
  { key: 'sop_pct',   label: 'SOP %',    align: 'right',  render: fmtPct },
  { key: 'doi',       label: 'DOI',      align: 'center', render: v => v ?? '—' },
  { key: 'total_cn',  label: 'Total CN', align: 'right',  render: fmtV },
  { key: 'expiry',    label: 'Expiry',   align: 'right',  render: fmtV },
  { key: 'returns',   label: 'Returns',  align: 'right',  render: fmtV },
  { key: 'rate_diff', label: 'Rate Dif', align: 'right',  render: fmtV },
  { key: 'breakage',  label: 'Breakage', align: 'right',  render: fmtV },
];

const buildBrandCols = fmtV => [
  { key: 'brand_name', label: 'Brand',    align: 'left' },
  { key: 'ly_sales',   label: 'LY Sales', align: 'right',  render: fmtV },
  { key: 'target',     label: 'Target',   align: 'right',  render: fmtV },
  { key: 'cy_sales',   label: 'CY Sales', align: 'right',  render: fmtV },
  { key: 'ach_pct',    label: 'Ach %',    align: 'right',  render: fmtPct },
  { key: 'grth_pct',   label: 'Grth %',   align: 'right',  render: fmtPct },
  { key: 'pcpm',       label: 'PCPM',     align: 'right',  render: fmtV },
  { key: 'sop_pct',    label: 'SOP %',    align: 'right',  render: fmtPct },
  { key: 'doi',        label: 'DOI',      align: 'center', render: v => v ?? '0' },
  { key: 'total_cn',   label: 'Total CN', align: 'right',  render: fmtV },
  { key: 'expiry',     label: 'Expiry',   align: 'right',  render: fmtV },
  { key: 'returns',    label: 'Returns',  align: 'right',  render: fmtV },
  { key: 'rate_diff',  label: 'Rate Dif', align: 'right',  render: fmtV },
  { key: 'breakage',   label: 'Breakage', align: 'right',  render: fmtV },
];

const buildHqCols = fmtV => [
  { key: 'hq_name',   label: 'HQ',       align: 'left' },
  { key: 'ly_sales',  label: 'LY Sales', align: 'right',  render: fmtV },
  { key: 'target',    label: 'Target',   align: 'right',  render: fmtV },
  { key: 'cy_sales',  label: 'CY Sales', align: 'right',  render: fmtV },
  { key: 'ach_pct',   label: 'Ach %',    align: 'right',  render: fmtPct },
  { key: 'grth_pct',  label: 'Grth %',   align: 'right',  render: fmtPct },
  { key: 'pcpm',      label: 'PCPM',     align: 'right',  render: fmtV },
  { key: 'sop_pct',   label: 'SOP %',    align: 'right',  render: fmtPct },
  { key: 'doi',       label: 'DOI',      align: 'center', render: v => v ?? '0' },
  { key: 'total_cn',  label: 'Total CN', align: 'right',  render: fmtV },
  { key: 'expiry',    label: 'Expiry',   align: 'right',  render: fmtV },
  { key: 'returns',   label: 'Returns',  align: 'right',  render: fmtV },
  { key: 'rate_diff', label: 'Rate Dif', align: 'right',  render: fmtV },
  { key: 'breakage',  label: 'Breakage', align: 'right',  render: fmtV },
];

const buildStateCols = fmtV => [
  { key: 'state',     label: 'State',    align: 'left' },
  { key: 'ly_sales',  label: 'LY Sales', align: 'right',  render: fmtV },
  { key: 'target',    label: 'Target',   align: 'right',  render: fmtV },
  { key: 'cy_sales',  label: 'CY Sales', align: 'right',  render: fmtV },
  { key: 'ach_pct',   label: 'Ach %',    align: 'right',  render: fmtPct },
  { key: 'grth_pct',  label: 'Grth %',   align: 'right',  render: fmtPct },
  { key: 'pcpm',      label: 'PCPM',     align: 'right',  render: fmtV },
  { key: 'sop_pct',   label: 'SOP %',    align: 'right',  render: fmtPct },
  { key: 'doi',       label: 'DOI',      align: 'center', render: v => v ?? '0' },
  { key: 'total_cn',  label: 'Total CN', align: 'right',  render: fmtV },
  { key: 'expiry',    label: 'Expiry',   align: 'right',  render: fmtV },
  { key: 'returns',   label: 'Returns',  align: 'right',  render: fmtV },
  { key: 'rate_diff', label: 'Rate Dif', align: 'right',  render: fmtV },
  { key: 'breakage',  label: 'Breakage', align: 'right',  render: fmtV },
];

// ── Sub-components ────────────────────────────────────────────────────────────
const GrowthBadge = ({ value }) => {
  if (value == null) return null;
  const up = value > 0;
  return (
    <span className={`growth-badge ${up ? 'growth-badge--up' : 'growth-badge--down'}`}>
      {up ? '▲' : '▼'} {up ? '' : '-'}{Math.abs(value).toFixed(1)}%
    </span>
  );
};

const TMSCoverageBadge = ({ achieved, total }) => {
  if (!total) return <span className="tms-badge tms-badge--empty">No data</span>;
  const pct       = (achieved / total) * 100;
  const isHealthy = pct >= 80;
  return (
    <div className="tms-coverage">
      <div className={`tms-badge ${isHealthy ? 'tms-badge--healthy' : 'tms-badge--warning'}`}>
        {pct.toFixed(0)}%
      </div>
      <div className="tms-progress-bar">
        <div
          className="tms-progress-fill"
          style={{
            width:      `${Math.min(pct, 100)}%`,
            background: isHealthy ? 'var(--green)' : 'var(--orange)',
          }}
        />
      </div>
    </div>
  );
};

const EmptyState = () => (
  <div className="empty-state">
    <div className="empty-state__icon">📊</div>
    <p className="empty-state__title">Please select filters to load the dashboard</p>
    <p className="empty-state__sub">Start by choosing a Division above</p>
  </div>
);

const PERFORMANCE_PAGE_SIZE = 10;

// ── Dashboard ─────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const [tableTab, setTableTab] = useState('monthly');
  const [achView,  setAchView]  = useState('bdm');
  const [unit,     setUnit]     = useState('lakh');
  const fmtV = v => formatVal(v, unit);

  const { hasActiveFilters, filters } = useFilters();

  // Auto-switch to Monthly tab when a month is selected
  useEffect(() => {
    if (filters.yearmonth) setTableTab('monthly');
  }, [filters.yearmonth]);

  // ── Data fetching ──────────────────────────────────────────────────────────
  const getMonthPerfFY = useCallback(
    (f) => getMonthPerformance({ ...f, yearmonth: undefined }),
    [],
  );

  // All data loads immediately once filters are active
  const { data: summary,      loading: sumLoading,   error: sumError } = useFetchData(getSalesSummary);
  const { data: rawTrends,    loading: trendLoading }                   = useFetchData(getMonthPerfFY);

  // ── Stockist — paginated fetch ─────────────────────────────────────────────
  const STOCKIST_PAGE_SIZE = 25;
  const [stockistPage,    setStockistPage]    = useState(1);
  const [stockistRows,    setStockistRows]    = useState([]);
  const [stockistTotal,   setStockistTotal]   = useState(0);
  const [stockistLoading, setStockistLoading] = useState(false);
  const stockistFetchRef = useRef(0);

  const fetchStockistPage = useCallback(async (pg, currentFilters) => {
    if (!currentFilters.division || !currentFilters.fiscalYear) {
      stockistFetchRef.current += 1;
      setStockistRows([]); setStockistTotal(0); setStockistLoading(false); return;
    }
    const token = ++stockistFetchRef.current;
    setStockistLoading(true);
    try {
      const result = await getStockistPerformance(currentFilters, pg, STOCKIST_PAGE_SIZE);
      if (token !== stockistFetchRef.current) return;
      setStockistRows(result.rows   ?? []);
      setStockistTotal(result.total ?? 0);
      setStockistPage(result.page   ?? pg);
    } catch (err) {
      console.error('[Stockist] fetch error:', err);
      if (token === stockistFetchRef.current) { setStockistRows([]); setStockistTotal(0); }
    } finally {
      if (token === stockistFetchRef.current) setStockistLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasActiveFilters) {
      stockistFetchRef.current += 1;
      setStockistRows([]); setStockistTotal(0); setStockistLoading(false); return;
    }
    setStockistPage(1);
    fetchStockistPage(1, filters);
  }, [filters, hasActiveFilters, fetchStockistPage]);

  const handleStockistPageChange = useCallback((pg) => {
    setStockistPage(pg);
    fetchStockistPage(pg, filters);
  }, [filters, fetchStockistPage]);

  // Performance tables are paged so large API responses do not lock the UI.
  const [bdmPage,    setBdmPage]    = useState(1);
  const [bdmRows,    setBdmRows]    = useState([]);
  const [bdmTotal,   setBdmTotal]   = useState(0);
  const [bdmLoading, setBdmLoading] = useState(false);
  const bdmFetchRef = useRef(0);

  const fetchBdmPage = useCallback(async (pg, currentFilters) => {
    if (!currentFilters.division || !currentFilters.fiscalYear) {
      bdmFetchRef.current += 1;
      setBdmRows([]); setBdmTotal(0); setBdmLoading(false); return;
    }
    const token = ++bdmFetchRef.current;
    setBdmLoading(true);
    try {
      const result = await getBDMPerformance(currentFilters, pg, PERFORMANCE_PAGE_SIZE);
      if (token !== bdmFetchRef.current) return;
      setBdmRows(result.rows   ?? []);
      setBdmTotal(result.total ?? 0);
      setBdmPage(result.page   ?? pg);
    } catch (err) {
      console.error('[BDM] fetch error:', err);
      if (token === bdmFetchRef.current) { setBdmRows([]); setBdmTotal(0); }
    } finally {
      if (token === bdmFetchRef.current) setBdmLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasActiveFilters) {
      bdmFetchRef.current += 1;
      setBdmRows([]); setBdmTotal(0); setBdmLoading(false); return;
    }
    setBdmPage(1);
    fetchBdmPage(1, filters);
  }, [filters, hasActiveFilters, fetchBdmPage]);

  const handleBdmPageChange = useCallback((pg) => {
    setBdmPage(pg);
    fetchBdmPage(pg, filters);
  }, [filters, fetchBdmPage]);

  const [brandPage,    setBrandPage]    = useState(1);
  const [brandRows,    setBrandRows]    = useState([]);
  const [brandTotal,   setBrandTotal]   = useState(0);
  const [brandLoading, setBrandLoading] = useState(false);
  const brandFetchRef = useRef(0);

  const fetchBrandPage = useCallback(async (pg, currentFilters) => {
    if (!currentFilters.division || !currentFilters.fiscalYear) {
      brandFetchRef.current += 1;
      setBrandRows([]); setBrandTotal(0); setBrandLoading(false); return;
    }
    const token = ++brandFetchRef.current;
    setBrandLoading(true);
    try {
      const result = await getBrandPerformance(currentFilters, pg, PERFORMANCE_PAGE_SIZE);
      if (token !== brandFetchRef.current) return;
      setBrandRows(result.rows   ?? []);
      setBrandTotal(result.total ?? 0);
      setBrandPage(result.page   ?? pg);
    } catch (err) {
      console.error('[Brand] fetch error:', err);
      if (token === brandFetchRef.current) { setBrandRows([]); setBrandTotal(0); }
    } finally {
      if (token === brandFetchRef.current) setBrandLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasActiveFilters) {
      brandFetchRef.current += 1; setBrandRows([]); setBrandTotal(0); setBrandLoading(false);
      return;
    }
    setBrandPage(1);
    fetchBrandPage(1, filters);
  }, [filters, hasActiveFilters, fetchBrandPage]);

  const handleBrandPageChange = useCallback((pg) => {
    setBrandPage(pg);
    fetchBrandPage(pg, filters);
  }, [filters, fetchBrandPage]);

  const [statePage,    setStatePage]    = useState(1);
  const [stateRows,    setStateRows]    = useState([]);
  const [stateTotal,   setStateTotal]   = useState(0);
  const [stateLoading, setStateLoading] = useState(false);
  const stateFetchRef = useRef(0);

  const fetchStatePage = useCallback(async (pg, currentFilters) => {
    if (!currentFilters.division || !currentFilters.fiscalYear) {
      stateFetchRef.current += 1;
      setStateRows([]); setStateTotal(0); setStateLoading(false); return;
    }
    const token = ++stateFetchRef.current;
    setStateLoading(true);
    try {
      const result = await getStatePerformance(currentFilters, pg, PERFORMANCE_PAGE_SIZE);
      if (token !== stateFetchRef.current) return;
      setStateRows(result.rows   ?? []);
      setStateTotal(result.total ?? 0);
      setStatePage(result.page   ?? pg);
    } catch (err) {
      console.error('[State] fetch error:', err);
      if (token === stateFetchRef.current) { setStateRows([]); setStateTotal(0); }
    } finally {
      if (token === stateFetchRef.current) setStateLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasActiveFilters) {
      stateFetchRef.current += 1; setStateRows([]); setStateTotal(0); setStateLoading(false);
      return;
    }
    setStatePage(1);
    fetchStatePage(1, filters);
  }, [filters, hasActiveFilters, fetchStatePage]);

  const handleStatePageChange = useCallback((pg) => {
    setStatePage(pg);
    fetchStatePage(pg, filters);
  }, [filters, fetchStatePage]);

  // ── HQ — manual paginated fetch (getHQPerformance returns {rows,total,page,page_size}) ──
  const [hqPage,    setHqPage]    = useState(1);
  const [hqRows,    setHqRows]    = useState([]);
  const [hqTotal,   setHqTotal]   = useState(0);
  const [hqLoading, setHqLoading] = useState(false);
  const hqFetchRef = useRef(0);

  const fetchHqPage = useCallback(async (pg, currentFilters) => {
    if (!currentFilters.division || !currentFilters.fiscalYear) {
      hqFetchRef.current += 1;
      setHqRows([]); setHqTotal(0); setHqLoading(false); return;
    }
    const token = ++hqFetchRef.current;
    setHqLoading(true);
    try {
      const result = await getHQPerformance(currentFilters, pg, PERFORMANCE_PAGE_SIZE);
      if (token !== hqFetchRef.current) return;
      setHqRows(result.rows   ?? []);
      setHqTotal(result.total ?? 0);
      setHqPage(result.page   ?? pg);
    } catch (err) {
      console.error('[HQ] fetch error:', err);
      if (token === hqFetchRef.current) { setHqRows([]); setHqTotal(0); }
    } finally {
      if (token === hqFetchRef.current) setHqLoading(false);
    }
  }, []);

  // Stable key for HQ re-fetch — avoids firing on every object reference change
  const hqFilterKey = JSON.stringify({
    division: filters.division,
    fiscalYear: filters.fiscalYear,
    yearmonth: filters.yearmonth,
    bdm: filters.bdm,
    zm: filters.zm,
    hq: filters.hq,
    brand: filters.brand,
  });

  // Re-fetch page 1 whenever filters change (debounced via timeout)
  useEffect(() => {
    if (!hasActiveFilters) {
      hqFetchRef.current += 1; setHqRows([]); setHqTotal(0); setHqLoading(false);
      return;
    }

    const timer = setTimeout(() => {
      setHqPage(1);
      fetchHqPage(1, filters);
    }, 300);

    return () => clearTimeout(timer);
  }, [hqFilterKey, hasActiveFilters, fetchHqPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleHqPageChange = useCallback((pg) => {
    setHqPage(pg);
    fetchHqPage(pg, filters);
  }, [filters, fetchHqPage]);

  // ── Exp % to Sales ────────────────────────────────────────────────────────
  const expPctToSales = (() => {
    if (!rawTrends?.length) return 0;
    const rows = (() => {
      if (!filters.yearmonth) return rawTrends;
      const ym  = String(filters.yearmonth).replace(/-/g, '');
      const row = rawTrends.find(r => String(r.yearmonth || '').replace(/-/g, '') === ym);
      return row ? [row] : rawTrends;
    })();
    const totalExpiry = rows.reduce((s, r) => s + Math.abs(r.expiry_value ?? 0), 0);
    const totalCy     = rows.reduce((s, r) => s + (r.cy_sales ?? 0), 0);
    return totalCy > 0 ? +((totalExpiry / totalCy) * 100).toFixed(2) : 0;
  })();

  // ── Monthly table rows ─────────────────────────────────────────────────────
  const monthlyData = rawTrends?.map(r => {
    const cy     = r.cy_sales ?? 0;
    const ly     = r.ly_sales ?? 0;
    const target = r.target   ?? 0;
    const emp    = r.employee_count ?? 0;
    const sop    = r.sop_count ?? 0;
    const ttms   = r.total_tms ?? 0;
    const total_cn = Math.abs(r.rate_difference ?? 0) + Math.abs(r.sales_return_value ?? 0)
                   + Math.abs(r.expiry_value ?? 0) + Math.abs(r.breakage_value ?? 0);
    const exp_pct = cy > 0 ? +((Math.abs(r.expiry_value ?? 0) / cy) * 100).toFixed(2) : 0;
    return {
      month:     r.month,
      ly_sales:  ly,
      target,
      cy_sales:  cy,
      ach_pct:   target > 0 ? +((cy / target) * 100).toFixed(1) : 0,
      grth_pct:  ly     > 0 ? +(((cy - ly) / ly) * 100).toFixed(1) : 0,
      pcpm:      emp    > 0 ? +(cy / emp).toFixed(2) : 0,
      sop_pct:   ttms   > 0 ? +(sop / ttms * 100).toFixed(1) : 0,
      doi:       0,
      total_cn,
      expiry:    Math.abs(r.expiry_value       ?? 0),
      exp_pct,
      returns:   Math.abs(r.sales_return_value ?? 0),
      rate_diff: Math.abs(r.rate_difference    ?? 0),
      breakage:  Math.abs(r.breakage_value     ?? 0),
    };
  }) ?? null;

  // ── Brand table rows ───────────────────────────────────────────────────────
  const brandTableData = brandRows?.map(r => {
    const cy = r.cy_sales ?? 0, ly = r.ly_sales ?? 0, target = r.target ?? 0;
    const emp = r.employee_count ?? 0;
    return {
      brand_name: r.brand_name,
      ly_sales: ly, target, cy_sales: cy,
      ach_pct:  target > 0 ? +((cy / target) * 100).toFixed(1) : 0,
      grth_pct: ly     > 0 ? +(((cy - ly) / ly) * 100).toFixed(1) : 0,
      pcpm:     emp    > 0 ? +(cy / emp).toFixed(2) : 0,
      sop_pct:  r.sop_pct ?? 0,
      doi: 0,
      total_cn: r.total_cn ?? 0,
      expiry:   r.expiry   ?? 0,
      returns:  r.returns  ?? 0,
      rate_diff: r.rate_diff ?? 0,
      breakage: r.breakage  ?? 0,
    };
  }) ?? null;

  // ── State table rows ───────────────────────────────────────────────────────
  const stateTableData = stateRows?.map(r => {
    const cy = r.cy_sales ?? 0, ly = r.ly_sales ?? 0, target = r.target ?? 0;
    const emp = r.employee_count ?? 0;
    return {
      state: r.state,
      ly_sales: ly, target, cy_sales: cy,
      ach_pct:  target > 0 ? +((cy / target) * 100).toFixed(1) : 0,
      grth_pct: ly     > 0 ? +(((cy - ly) / ly) * 100).toFixed(1) : 0,
      pcpm:     emp    > 0 ? +(cy / emp).toFixed(2) : 0,
      sop_pct:  r.sop_pct ?? 0,
      doi: 0,
      total_cn: r.total_cn ?? 0,
      expiry:   r.expiry   ?? 0,
      returns:  r.returns  ?? 0,
      rate_diff: r.rate_diff ?? 0,
      breakage: r.breakage  ?? 0,
    };
  }) ?? null;

  // ── Highlight selected month in Monthly table ──────────────────────────────
  const highlightMonthLabel = (() => {
    if (!filters.yearmonth || !rawTrends?.length) return null;
    try {
      const ym    = filters.yearmonth;
      const year  = parseInt(ym.substring(0, 4));
      const month = parseInt(ym.substring(4, 6));
      return new Date(year, month - 1, 1)
        .toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } catch { return null; }
  })();

  // ── Table config ───────────────────────────────────────────────────────────
  const tableConfig = {
    monthly: { cols: buildMonthlyCols(fmtV), data: monthlyData,    loading: trendLoading, highlight: highlightMonthLabel, paginated: false },
    bdm:     { cols: buildBdmCols(fmtV),     data: bdmRows,        loading: bdmLoading,   highlight: null,                paginated: true, total: bdmTotal,   page: bdmPage,   onPageChange: handleBdmPageChange },
    brand:   { cols: buildBrandCols(fmtV),   data: brandTableData, loading: brandLoading, highlight: null,                paginated: true, total: brandTotal, page: brandPage, onPageChange: handleBrandPageChange },
    hq:      { cols: buildHqCols(fmtV),      data: hqRows,         loading: hqLoading,    highlight: null,                paginated: true, total: hqTotal,    page: hqPage,    onPageChange: handleHqPageChange },
    state:   { cols: buildStateCols(fmtV),   data: stateTableData, loading: stateLoading, highlight: null,                paginated: true, total: stateTotal, page: statePage, onPageChange: handleStatePageChange },
  };
  const active = tableConfig[tableTab] ?? tableConfig.monthly;

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <div className="dashboard">

      {/* ── Header ── */}
      <header className="dashboard-header">
        <div className="dashboard-header__left">
          <div className="dashboard-header__logo-icon">📊</div>
          <div>
            <div className="dashboard-header__title">Division Performance Dashboard</div>
            <div className="dashboard-header__subtitle">Emcure Pharmaceuticals</div>
          </div>
        </div>
        <div className="dashboard-header__right">
          <span className="dashboard-header__badge">
            {filters.fiscalYear ? `FY ${filters.fiscalYear}` : 'FY 2025–26'}
          </span>
          <div className="dashboard-header__brand">
            Emcure<span>cure and beyond</span>
          </div>
        </div>
      </header>

      <div className="dashboard__content">
        <FilterBar />

        {!hasActiveFilters ? (
          <EmptyState />
        ) : (
          <>
            {/* ── KPI Row ── */}
            <section className="dashboard__section">
              <div className="dashboard__section-header">
                <span className="dashboard__section-title">Key Performance Indicators</span>
                <div className="tab-group">
                  {[['absolute', 'Abs'], ['lakh', 'Lakh'], ['crore', 'Cr']].map(([val, label]) => (
                    <button key={val} className={`tab-btn ${unit === val ? 'tab-btn--active' : ''}`} onClick={() => setUnit(val)}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="dashboard__section-body">
                {sumError && <div className="error-banner">⚠ {sumError}</div>}
                {sumLoading ? (
                  <div className="kpi-skeleton">
                    {[...Array(7)].map((_, i) => <div key={i} className="kpi-skeleton__item" />)}
                  </div>
                ) : summary ? (
                  <div className="kpi-grid">
                    <KPIBox
                      label="Achievement %"
                      value={
                        <span className="kpi-donut-wrap">
                          <svg className="kpi-donut" viewBox="0 0 36 36">
                            <circle cx="18" cy="18" r="15.9" fill="none" stroke="var(--border)" strokeWidth="3" />
                            <circle cx="18" cy="18" r="15.9" fill="none"
                              stroke={summary.achievement_pct >= 100 ? 'var(--green)' : summary.achievement_pct >= 85 ? 'var(--orange)' : 'var(--red)'}
                              strokeWidth="3"
                              strokeDasharray={`${Math.min(summary.achievement_pct, 100)} ${Math.max(0, 100 - summary.achievement_pct)}`}
                              strokeDashoffset="25" strokeLinecap="round"
                            />
                            <text x="18" y="20.5" textAnchor="middle" fontSize="8" fontWeight="700"
                              fill="var(--text-primary)" transform="rotate(90 18 18)">
                              {summary.achievement_pct}%
                            </text>
                          </svg>
                        </span>
                      }
                      sub={`Target: ${(summary.target / 100000).toFixed(1)}L`}
                      variant="default"
                    />
                    <KPIBox
                      label="Growth %"
                      value={<GrowthBadge value={summary.growth_pct} />}
                      sub={`LY: ${(summary.ly_sales / 100000).toFixed(1)}L`}
                      variant={summary.growth_pct >= 0 ? 'success' : 'danger'}
                    />
                    <KPIBox
                      label="TMs Achieved"
                      value={<TMSCoverageBadge achieved={summary.tms_achieved ?? 0} total={summary.total_tms ?? 0} />}
                      sub={(summary.total_tms ?? 0) > 0 ? `${summary.tms_achieved} of ${summary.total_tms} TMs` : 'No TMs data'}
                      variant={(summary.total_tms ?? 0) > 0 && (summary.tms_achieved / summary.total_tms) >= 0.8 ? 'success' : 'warn'}
                      icon="👥"
                    />
                    <KPIBox
                      label="SOP %"
                      value={`${summary.sop_pct ?? 0}%`}
                      sub={(summary.total_tms ?? 0) > 0 ? `${summary.sop_count ?? 0} of ${summary.total_tms} HQs` : 'HQs exceeding budget'}
                      variant={(summary.sop_pct ?? 0) >= 80 ? 'success' : 'warn'}
                      icon="📦"
                    />
                    <KPIBox
                      label="Total CN %"
                      value={`${summary.total_cn_pct ?? 0}%`}
                      sub="Credit Notes"
                      variant={(summary.total_cn_pct ?? 0) > 5 ? 'danger' : 'warn'}
                      icon="📋"
                    />
                    <KPIBox
                      label="Exp % to Sales"
                      value={`${expPctToSales}%`}
                      sub="Expiry ratio"
                      variant={expPctToSales > 2 ? 'danger' : 'success'}
                      icon="⏳"
                    />
                    <KPIBox
                      label="PCPM"
                      value={summary.pcpm ?? 0}
                      sub="Per Capita Per Month"
                      variant="info"
                      icon="₹"
                    />
                  </div>
                ) : null}
              </div>
            </section>

            {/* ── Charts Row ── */}
            <div className="charts-row">
              <section className="dashboard__section">
                <div className="dashboard__section-header">
                  <span className="dashboard__section-title">Sales Trend</span>
                  {(filters.fiscalYear || filters.yearmonthLabel) && (
                    <span className="dashboard__section-badge">
                      {filters.yearmonthLabel || (() => {
                        const [s, e] = filters.fiscalYear.split('-');
                        return `Apr'${s.slice(2)} – Mar'${e}`;
                      })()}
                    </span>
                  )}
                </div>
                <div className="dashboard__section-body">
                  <SalesTrendChart data={rawTrends} loading={trendLoading} />
                </div>
              </section>

              <section className="dashboard__section">
                <div className="dashboard__section-header">
                  <span className="dashboard__section-title">ACH %</span>
                  <div className="tab-group">
                    {[['bdm', 'BDM'], ['brand', 'Brand'], ['state', 'State']].map(([val, label]) => (
                      <button key={val} className={`tab-btn ${achView === val ? 'tab-btn--active' : ''}`} onClick={() => setAchView(val)}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="dashboard__section-body">
                  <AchChart
                    monthlyData={rawTrends}
                    bdmData={bdmRows}
                    brandData={brandRows}
                    stateData={stateRows}
                    loading={trendLoading || bdmLoading || brandLoading || stateLoading}
                    view={achView}
                  />
                </div>
              </section>
            </div>

            {/* ── Performance Details (tabbed table) ── */}
            <section className="dashboard__section">
              <div className="dashboard__section-header">
                <span className="dashboard__section-title">Performance Details</span>
                <div className="tab-group">
                  {['monthly', 'bdm', 'brand', 'hq', 'state'].map(tab => (
                    <button
                      key={tab}
                      className={`tab-btn ${tableTab === tab ? 'tab-btn--active' : ''}`}
                      onClick={() => setTableTab(tab)}
                    >
                      {tab === 'hq' ? 'HQ' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="dashboard__section-body dashboard__section-body--table">
                <DataTable
                  columns={active.cols}
                  data={active.data}
                  loading={active.loading}
                  highlightRow={active.highlight}
                  totalRows={active.paginated ? active.total : undefined}
                  page={active.paginated ? active.page : undefined}
                  pageSize={active.paginated ? PERFORMANCE_PAGE_SIZE : undefined}
                  onPageChange={active.paginated ? active.onPageChange : undefined}
                />
              </div>
            </section>

            {/* ── Stockist Performance ── */}
            <section className="dashboard__section">
              <div className="dashboard__section-header">
                <span className="dashboard__section-title">Stockist Performance</span>
                <span className="dashboard__section-badge">Current Month vs L6M Avg</span>
              </div>
              <div className="dashboard__section-body dashboard__section-body--table">
                <StockistTable
                  data={stockistRows}
                  loading={stockistLoading}
                  totalRows={stockistTotal}
                  page={stockistPage}
                  pageSize={STOCKIST_PAGE_SIZE}
                  onPageChange={handleStockistPageChange}
                />
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
